import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import dayjs from 'dayjs'

import { TaskDefinition } from '@/feature/execution-board/types/task'
import { Organization } from '@/feature/observatory'

import { DATE_FORMAT_API } from '@/lib/core/constants/constants'

import styles from './ExecutionBoardFilters.module.css'
import { StatusFilterPopout } from '../StatusFilterPopup/StatusFilterPopup'
import { TaskStatus } from '../CreateTaskModal'
import { TaskFilters } from '../../api/useTasks'
import { TaskOwnerFilterPopout } from '../TaskOwnerFilterPopout'
import { DateRangeFilterPopout } from '../DateRangeFilterPopout'
import { AssigneeFilterPopout } from '../AssigneeFilterPopout'

export const supportedUrls = ['/execution-board/all']

export enum OpeningFilterPopout {
  DATE_RANGE = 'dateRange',
  STATUS = 'status',
  TASK_OWNER = 'taskOwner',
  ASSIGNED_TO = 'assignedTo',
}

export const ExecutionBoardFilters = ({
  onChangeFilter,
  tasks,
  filters,
}: {
  onChangeFilter: (filter: TaskFilters) => void
  filters: TaskFilters
  tasks?: TaskDefinition[]
}) => {
  const router = useRouter()
  const { asPath } = router

  const [availableTaskOwners, setAvailableTaskOwners] = useState<Organization[]>()
  const [availableAssignees, setAvailableAssignees] = useState<Organization[]>()
  const [openingFilterPopout, setOpeningFilterPopout] = useState<OpeningFilterPopout | null>(null)

  const isSupported = supportedUrls.some(
    (prefix) => asPath.startsWith(prefix) || asPath.split('?')[0] === prefix,
  )

  const onSetFilteringStatuses = (statuses: TaskStatus[] | null) => {
    if (statuses) {
      onChangeFilter({ status: statuses })
    } else {
      onChangeFilter({ status: undefined })
    }
  }

  const onSetFilteringTaskOwner = (orgId: number | null) => {
    if (orgId) {
      onChangeFilter({ orgFrom: orgId })
    } else {
      onChangeFilter({ orgFrom: undefined })
    }
  }

  const onSetFilteringAssignee = (orgId: number | null) => {
    if (orgId) {
      onChangeFilter({ orgTo: orgId })
    } else {
      onChangeFilter({ orgTo: undefined })
    }
  }

  const onSetFilteringDateRange = (dateRange: { start: Date; end: Date } | null) => {
    if (dateRange) {
      onChangeFilter({
        fromDatetime: dayjs(dateRange.start).format(DATE_FORMAT_API),
        toDatetime: dayjs(dateRange.end).format(DATE_FORMAT_API),
      })
    } else {
      onChangeFilter({
        fromDatetime: undefined,
        toDatetime: undefined,
      })
    }
  }

  const onOpenFilterPopout = (popout: OpeningFilterPopout) => {
    setOpeningFilterPopout(popout)
  }

  useEffect(() => {
    // Only set in the first time with full tasks
    if (tasks?.length && !availableTaskOwners) {
      const seen = new Map()
      const owners: Organization[] = tasks.map((task) => task.assignedByOrg)
      setAvailableTaskOwners(
        owners.filter((org) => {
          const keyValue = org['id']
          if (!seen.has(keyValue)) {
            seen.set(keyValue, true)
            return true
          }
          return false
        }),
      )
    }
  }, [tasks])

  useEffect(() => {
    // Only set in the first time with full tasks
    if (tasks?.length && !availableAssignees) {
      const seen = new Map()
      const assignees = tasks
        .filter((task) => task.onwardAssignedToOrg) // Assigned To field can be null
        .map((task) => task.onwardAssignedToOrg) as Organization[]
      setAvailableAssignees(
        assignees.filter((org) => {
          const keyValue = org['id']
          if (!seen.has(keyValue)) {
            seen.set(keyValue, true)
            return true
          }
          return false
        }),
      )
    }
  }, [tasks])

  if (!isSupported || !availableTaskOwners || !availableAssignees) {
    return null
  }

  return (
    <span className={styles.wrapper} data-testid="execution-board-header">
      <DateRangeFilterPopout
        setFilteringDateRange={onSetFilteringDateRange}
        currentDateRange={{
          start: filters.fromDatetime ? dayjs(filters.fromDatetime).toDate() : undefined,
          end: filters.fromDatetime ? dayjs(filters.toDatetime).toDate() : undefined,
        }}
        openingFilterPopout={openingFilterPopout}
        onOpenFilterPopout={onOpenFilterPopout}
      />
      <StatusFilterPopout
        setFilteringStatuses={onSetFilteringStatuses}
        currentSelectedStatuses={filters.status as TaskStatus[]}
        openingFilterPopout={openingFilterPopout}
        onOpenFilterPopout={onOpenFilterPopout}
      />
      <TaskOwnerFilterPopout
        setFilteringTaskOwner={onSetFilteringTaskOwner}
        availableTaskOwners={availableTaskOwners}
        currentSelectedOrgId={filters.orgFrom}
        openingFilterPopout={openingFilterPopout}
        onOpenFilterPopout={onOpenFilterPopout}
      />
      <AssigneeFilterPopout
        setFilteringAssignee={onSetFilteringAssignee}
        availableAssignees={availableAssignees}
        currentSelectedOrgId={filters.orgTo}
        openingFilterPopout={openingFilterPopout}
        onOpenFilterPopout={onOpenFilterPopout}
      />
    </span>
  )
}
